<template>
  <div
    style="width: 100%"
    :class="{'d-flex align-center': withExportButton}"
  >
    <RegisteredField
      v-if="isFormElement"
      v-slot="{ errors }"
      :vid="vid"
      :name="label"
      :rules="rules"
      class="flex-grow-1"
    >
      <keep-alive>
        <component
          ref="dynamicComponent" 
          :is="dynamicComponent"
          v-bind="genericPickerProps"
          :path="path"
          :errors="errors"
          :exclude-ids="excludeIds"
          v-on="$listeners"
          @click:openDialog="handleOpenDialog"
        >
          <template
            v-if="$scopedSlots.item"
            #item="{ item }"
          >
            <slot
              name="item"
              :item="item"
            />
          </template>

          <template #tooltip>
            <slot name="tooltip" />
          </template>

          <template #append-outer-actions>
            <slot name="append-outer-actions" />
          </template>
        </component>
      </keep-alive>
    </RegisteredField>

    <keep-alive v-else>
      <component
        ref="dynamicComponent"
        :is="dynamicComponent"
        v-bind="genericPickerProps"
        :path="path"
        :errors="errors"
        :exclude-ids="excludeIds"
        class="flex-grow-1"
        v-on="$listeners"
        @click:openDialog="handleOpenDialog"
      >
        <template
          v-if="$scopedSlots.item"
          #item="{ item }"
        >
          <slot
            name="item"
            :item="item"
          />
        </template>

        <template #tooltip>
          <slot name="tooltip" />
        </template>

        <template #append-outer-actions>
          <slot name="append-outer-actions" />
        </template>
      </component>
    </keep-alive>

    <w-export-button
      v-if="withExportButton && hasExportId"
      @click="navigateToEntry"
    >
      <template #tooltip>
        {{ $t('Components.sentences.navigateToEntry', [label]) }}
      </template>
    </w-export-button>

    <GenericPickerDialog
      ref="genericDialog"
      v-bind="genericDialogProps"
      :path="path"
      :exclude-ids="excludeIds"
      :select-id="selectId"
    >
      <template #header>
        <slot name="header" />
      </template>

      <template #append-search-expression-filter>
        <slot name="append-search-expression-filter" />
      </template>

      <template #filters="{ filtersData }">
        <slot
          name="filters"
          :filters-data="filtersData"
        />
      </template>
    </GenericPickerDialog>
  </div>
</template>

<script>
// components
import GenericPickerWithInputField from '@/components/global_components/pickers/generic_picker/components/GenericPickerWithInputField';
import GenericPickerOnlyInputField from '@/components/global_components/pickers/generic_picker/components/GenericPickerOnlyInputField';
import DefaultGenericPicker from '@/components/global_components/pickers/generic_picker/components/DefaultGenericPicker';

// mixins
import genericPickerProps from '@/components/global_components/pickers/generic_picker/mixins/generic-picker-props';
import genericPickerDialogProps from '@/components/global_components/pickers/generic_picker/mixins/generic-picker-dialog-props';

// services & utils
import { filterProps } from '@/utils/functional-component-utils';
import i18n from '@/language/i18n';
import get from 'lodash/get';

// store
import { mapActions } from 'vuex';
import RegisteredField from '@/validation/RegisteredField';

const isInApiObjectFormat = (item) =>
  item.id != undefined && item.data != undefined;
const DEFAULT_ERROR = (label) => ({
  fieldName: label,
  message: i18n.t('Components.sentences.personNotFound'),
});
const NECESSARY_PROPS = ['path', 'mandatoryFilters', 'filterPreset'];
const isArray = (v) => Array.isArray(v);

export default {
  name: 'WorkGenericPicker',
  components: {
    RegisteredField,
    GenericPickerDialog: () =>
      import(
        '@/components/global_components/pickers/generic_picker/dialogs/GenericPickerDialog'
      ),
  },
  mixins: [genericPickerProps, genericPickerDialogProps],
  computed: {
    dynamicComponent() {
      if (this.withInputField) {
        return GenericPickerWithInputField;
      }
      if (this.onlyInputField) {
        return GenericPickerOnlyInputField;
      }
      return DefaultGenericPicker;
    },
    genericPickerProps() {
      return filterProps(
        this.$props,
        this._getFilterableProps(genericPickerDialogProps)
      );
    },
    genericDialogProps() {
      return filterProps(
        this.$props,
        this._getFilterableProps(genericPickerProps)
      );
    },
    hasExportId(){
      if (this.withExportButton) {
        return get(this.value, this.exportValue) != undefined
      }
      return false;
    }
  },
  methods: {
    ...mapActions('errorNotificationStore', [
      'setErrors',
      'showErrorNotification',
    ]),
    async handleOpenDialog() {
      /** @type {Object|Array} **/
      let genericResult = await this.$refs.genericDialog.open();
      if (this.noDataReload) {
        return this._handleNewGenericEntityWithoutReload(genericResult);
      }
      return this._handleNewGenericEntityWithReload(genericResult);
    },
    _handleNewGenericEntityWithoutReload(genericResult) {
      if (isArray(genericResult)) {
        if (this.multiple) {
          let formattedMultipleData = genericResult.map((genericEntity) => ({
            id: String(genericEntity.id),
            data: { ...genericEntity },
          }));
          this._invokeInput(formattedMultipleData);
        }
        return this._invokeInput(genericResult);
      }
      this._invokeInput(this._formatToNecessaryFormat(genericResult));
    },
    async _handleNewGenericEntityWithReload(genericResult) {
      if (this.loadGenericId) {
        let loadedResult = await this._invokeLoadOfGenericResult(genericResult);
        this._invokeInput(loadedResult);
      } else {
        this._invokeInput({
          id: String(genericResult.id),
          data: { ...genericResult }
        })
      }
    },
    navigateToEntry(){
      const routeParams = { name: this.exportComponent, params: { id: get(this.value, this.exportValue) } }
      
      if (this.navigateWithNewTab) {
        this.$openUrlInNewTab(routeParams);
      }
      else 
        this.$router.push(routeParams);
    },
    /******************************
     * ### RELOAD FUNCTIONALITY ###
     *****************************/
    _handleError() {
      this.setErrors([DEFAULT_ERROR(this.label)]);
      this.showErrorNotification();
    },
    /**************************
     * ### INTERNAL HELPERS ###
     *************************/
    _formatToNecessaryFormat(genericEntity) {
      if (this.returnObject && !isInApiObjectFormat(genericEntity)) {
        return { id: String(genericEntity.id), data: { ...genericEntity } };
      }
      return String(genericEntity.id);
    },
    _getFilterableProps(mixin) {
      let byNecessaryProps = (propKey) => !NECESSARY_PROPS.includes(propKey);
      return Object.keys(mixin.props).filter(byNecessaryProps);
    },
    /****************
     * ### LOADIN ###
     ***************/
    async _invokeLoadOfGenericResult(genericResult) {
      if (Array.isArray(genericResult)) {
        return await this._loadArray(genericResult);
      }
      return await this._load(genericResult);
    },
    async _load(genericResult) {
      return await this.loadGenericId(genericResult.id);
    },
    async _loadArray(genericResult) {
      let loadedGenericEntities = [];

      for (let genericEntity of genericResult) {
        loadedGenericEntities.push(await this.loadGenericId(genericEntity.id));
      }

      return loadedGenericEntities;
    },
    reset() {
      if (this.$refs.dynamicComponent && this.$refs.dynamicComponent.reset) {
        this.$refs.dynamicComponent.reset();
      }
    },
    /************************
     * ### EVENT EMITTERS ###
     ***********************/
    _invokeInput(v) {
      this.$emit('input', v);
    },
  },
};
</script>
